import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const NAV_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/dashboard-overview',
    children: null,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'BarChart2',
    path: null,
    children: [
      { label: 'Performance Analytics', path: '/performance-analytics', icon: 'TrendingUp' },
      { label: 'AI Intelligence Reports', path: '/ai-intelligence-reports', icon: 'BrainCircuit' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: 'Briefcase',
    path: null,
    children: [
      { label: 'Account Management', path: '/account-management', icon: 'Users' },
      { label: 'Sales Ordering', path: '/sales-ordering-system', icon: 'ShoppingCart' },
    ],
  },
  {
    id: 'territory',
    label: 'Territory',
    icon: 'Map',
    path: null,
    children: [
      { label: 'Philippine Map', path: '/philippine-map-visualization', icon: 'MapPin' },
      { label: 'Sales Team Structure', path: '/territory-sales-team', icon: 'Network' },
    ],
  },
];

const MOBILE_NAV = [
  { label: 'Dashboard', path: '/dashboard-overview', icon: 'LayoutDashboard' },
  { label: 'Analytics', path: '/performance-analytics', icon: 'BarChart2' },
  { label: 'Operations', path: '/account-management', icon: 'Briefcase' },
  { label: 'Territory', path: '/philippine-map-visualization', icon: 'Map' },
];

export default function TopNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState(null);
  const dropdownRef = useRef({});
  const navRef = useRef(null);

  const isPathActive = (path) => location?.pathname === path;

  const isGroupActive = (group) => {
    if (group?.path) return isPathActive(group?.path);
    return group?.children?.some((c) => isPathActive(c?.path));
  };

  const handleGroupClick = (groupId, path) => {
    if (path) {
      navigate(path);
      setOpenGroup(null);
      return;
    }
    setOpenGroup((prev) => (prev === groupId ? null : groupId));
  };

  const handleNavItemClick = (path) => {
    navigate(path);
    setOpenGroup(null);
    setMobileOpen(false);
    setMobileExpandedGroup(null);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef?.current && !navRef?.current?.contains(e?.target)) {
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        setOpenGroup(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      {/* Main Header */}
      <header
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-[100] border-b border-border shadow-sm"
        style={{ backgroundColor: '#FFFFFF' }}
        role="banner"
      >
        <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-6">
          {/* Logo */}
          <div
            className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0"
            onClick={() => navigate('/dashboard-overview')}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e?.key === 'Enter' && navigate('/dashboard-overview')}
            aria-label="Go to Dashboard"
          >
            <div
              className="flex items-center justify-center rounded-md flex-shrink-0"
              style={{
                width: '32px',
                height: '32px',
                background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                borderRadius: '8px',
                boxShadow: '0 0 12px rgba(220, 38, 38, 0.4)',
              }}
            >
              <Icon name="Zap" size={17} color="#FFFFFF" strokeWidth={2} />
            </div>
            <div className="flex flex-col justify-center">
              <span
                className="font-heading leading-tight"
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #DC2626, #D4AF37)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.01em',
                }}
              >
                iSYNC
              </span>
              <p
                className="hidden sm:block leading-tight"
                style={{
                  fontSize: '10px',
                  color: '#475569',
                  marginTop: '1px',
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                }}
              >
                <span className="hidden lg:inline">Distribution Ordering &amp; Sales Intelligence System</span>
                <span className="inline lg:hidden" style={{ fontSize: '9px' }}>Dist. Ordering &amp; Sales Intelligence</span>
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav
            className="hidden lg:flex items-center gap-1"
            aria-label="Primary navigation"
          >
            {NAV_GROUPS?.map((group) => (
              <div key={group?.id} className="relative" ref={(el) => (dropdownRef.current[group.id] = el)}>
                <button
                  className={`nav-item press-effect focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
                    isGroupActive(group) ? 'active' : ''
                  }`}
                  onClick={() => handleGroupClick(group?.id, group?.path)}
                  aria-expanded={openGroup === group?.id}
                  aria-haspopup={group?.children ? 'true' : undefined}
                >
                  <Icon name={group?.icon} size={16} color="currentColor" strokeWidth={2} />
                  <span>{group?.label}</span>
                  {group?.children && (
                    <Icon
                      name="ChevronDown"
                      size={14}
                      color="currentColor"
                      strokeWidth={2}
                      className={`transition-transform duration-200 ${openGroup === group?.id ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>

                {/* Dropdown */}
                {group?.children && openGroup === group?.id && (
                  <div
                    className="dropdown-menu absolute top-full left-0 mt-2 min-w-[220px] py-2"
                    role="menu"
                    aria-label={`${group?.label} submenu`}
                  >
                    {group?.children?.map((child) => (
                      <button
                        key={child?.path}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left font-caption text-sm transition-all duration-200 hover:bg-muted press-effect focus:outline-none focus-visible:bg-muted ${
                          isPathActive(child?.path)
                            ? 'text-primary bg-muted font-medium' :'text-card-foreground'
                        }`}
                        onClick={() => handleNavItemClick(child?.path)}
                        role="menuitem"
                      >
                        <Icon name={child?.icon} size={16} color="currentColor" strokeWidth={2} />
                        <span>{child?.label}</span>
                        {isPathActive(child?.path) && (
                          <Icon name="Check" size={14} color="currentColor" strokeWidth={2.5} className="ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right Side: Territory Filter + User Profile */}
          <div className="flex items-center gap-2">
            <TerritoryFilterDropdown />
            <UserProfileMenu />

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={22} color="currentColor" strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[99]"
          style={{ backgroundColor: 'rgba(26, 26, 46, 0.3)', top: '64px' }}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Mobile Slide-out Menu */}
      <div
        className={`fixed top-16 left-0 right-0 z-[100] bg-card border-b border-border shadow-lg transition-all duration-250 lg:hidden overflow-hidden ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="px-4 py-3 custom-scrollbar overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          {NAV_GROUPS?.map((group) => (
            <div key={group?.id} className="mb-1">
              <button
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-caption font-medium text-sm transition-all duration-200 press-effect ${
                  isGroupActive(group)
                    ? 'text-primary bg-muted' :'text-card-foreground hover:bg-muted'
                }`}
                onClick={() => {
                  if (group?.path) {
                    handleNavItemClick(group?.path);
                  } else {
                    setMobileExpandedGroup((prev) => (prev === group?.id ? null : group?.id));
                  }
                }}
              >
                <Icon name={group?.icon} size={18} color="currentColor" strokeWidth={2} />
                <span className="flex-1 text-left">{group?.label}</span>
                {group?.children && (
                  <Icon
                    name="ChevronDown"
                    size={16}
                    color="currentColor"
                    strokeWidth={2}
                    className={`transition-transform duration-200 ${mobileExpandedGroup === group?.id ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {group?.children && mobileExpandedGroup === group?.id && (
                <div className="ml-4 mt-1 border-l-2 border-border pl-3">
                  {group?.children?.map((child) => (
                    <button
                      key={child?.path}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md font-caption text-sm transition-all duration-200 press-effect ${
                        isPathActive(child?.path)
                          ? 'text-primary bg-muted font-medium' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      onClick={() => handleNavItemClick(child?.path)}
                    >
                      <Icon name={child?.icon} size={16} color="currentColor" strokeWidth={2} />
                      <span>{child?.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Mobile Bottom Navigation */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-[100] bg-card border-t border-border"
        style={{ height: '60px' }}
        aria-label="Mobile bottom navigation"
      >
        <div className="flex items-center justify-around h-full px-2">
          {MOBILE_NAV?.map((item) => (
            <button
              key={item?.path}
              className={`flex flex-col items-center justify-center gap-1 min-w-[44px] min-h-[44px] px-3 rounded-md transition-all duration-200 press-effect focus:outline-none ${
                isPathActive(item?.path) ? 'text-primary' : 'text-muted-foreground'
              }`}
              onClick={() => navigate(item?.path)}
              aria-label={item?.label}
              aria-current={isPathActive(item?.path) ? 'page' : undefined}
            >
              <Icon name={item?.icon} size={20} color="currentColor" strokeWidth={isPathActive(item?.path) ? 2.5 : 2} />
              <span className="font-caption text-xs">{item?.label}</span>
            </button>
          ))}
        </div>
      </nav>
      {/* Spacer for fixed header */}
      <div style={{ height: '64px' }} aria-hidden="true" />
    </>
  );
}

/* ─────────────────────────────────────────────
   Territory Filter Dropdown (inline sub-component)
───────────────────────────────────────────── */
const TERRITORIES = [
  { value: 'all', label: 'All Territories', icon: 'Globe' },
  { value: 'ncr', label: 'NCR', icon: 'Building2' },
  { value: 'luzon', label: 'Luzon', icon: 'MapPin' },
  { value: 'visayas', label: 'Visayas', icon: 'MapPin' },
  { value: 'mindanao', label: 'Mindanao', icon: 'MapPin' },
];

function TerritoryFilterDropdown() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(TERRITORIES?.[0]);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative hidden sm:block">
      <button
        className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-background text-foreground font-caption text-sm font-medium transition-all duration-200 hover:bg-muted press-effect focus:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[36px]"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Select territory"
      >
        <Icon name="MapPin" size={14} color="var(--color-primary)" strokeWidth={2} />
        <span className="hidden md:inline max-w-[100px] truncate">{selected?.label}</span>
        <Icon
          name="ChevronDown"
          size={14}
          color="currentColor"
          strokeWidth={2}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          className="dropdown-menu absolute top-full right-0 mt-2 w-52 py-2"
          role="listbox"
          aria-label="Territory options"
        >
          <p className="px-4 py-1.5 font-caption text-xs text-muted-foreground uppercase tracking-wider font-medium">
            Filter by Territory
          </p>
          {TERRITORIES?.map((t) => (
            <button
              key={t?.value}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left font-caption text-sm transition-all duration-200 hover:bg-muted press-effect focus:outline-none focus-visible:bg-muted ${
                selected?.value === t?.value ? 'text-primary font-medium bg-muted' : 'text-card-foreground'
              }`}
              onClick={() => { setSelected(t); setOpen(false); }}
              role="option"
              aria-selected={selected?.value === t?.value}
            >
              <Icon name={t?.icon} size={15} color="currentColor" strokeWidth={2} />
              <span className="flex-1">{t?.label}</span>
              {selected?.value === t?.value && (
                <Icon name="Check" size={14} color="currentColor" strokeWidth={2.5} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   User Profile Menu (inline sub-component)
───────────────────────────────────────────── */
function UserProfileMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const user = {
    name: 'Maria Santos',
    role: 'National Manager',
    territory: 'All Territories',
    initials: 'MS',
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const menuItems = [
    { label: 'My Profile', icon: 'User', action: () => {} },
    { label: 'Account Settings', icon: 'Settings', action: () => {} },
    { label: 'Notifications', icon: 'Bell', action: () => {} },
    { label: 'Help & Support', icon: 'HelpCircle', action: () => {} },
    { label: 'Sign Out', icon: 'LogOut', action: () => {}, danger: true },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        className="flex items-center gap-2 px-2 py-1.5 rounded-md transition-all duration-200 hover:bg-muted press-effect focus:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[36px]"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="User profile menu"
      >
        <div
          className="flex items-center justify-center rounded-full font-heading font-600 text-sm text-primary-foreground flex-shrink-0"
          style={{
            width: '32px',
            height: '32px',
            background: 'var(--color-primary)',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {user?.initials}
        </div>
        <div className="hidden md:block text-left">
          <p className="font-caption text-sm font-medium text-foreground leading-tight">{user?.name}</p>
          <p className="font-caption text-xs text-muted-foreground leading-tight">{user?.role}</p>
        </div>
        <Icon
          name="ChevronDown"
          size={14}
          color="currentColor"
          strokeWidth={2}
          className={`hidden md:block text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          className="dropdown-menu absolute top-full right-0 mt-2 w-64 py-2"
          role="menu"
          aria-label="User account menu"
        >
          {/* User info header */}
          <div className="px-4 py-3 border-b border-border mb-1">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center rounded-full text-primary-foreground flex-shrink-0"
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--color-primary)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  fontFamily: 'var(--font-heading)',
                }}
              >
                {user?.initials}
              </div>
              <div>
                <p className="font-caption text-sm font-medium text-card-foreground">{user?.name}</p>
                <p className="font-caption text-xs text-muted-foreground">{user?.role}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <Icon name="MapPin" size={10} color="var(--color-primary)" strokeWidth={2} />
                  <span className="font-caption text-xs text-primary">{user?.territory}</span>
                </div>
              </div>
            </div>
          </div>

          {menuItems?.map((item, idx) => (
            <React.Fragment key={item?.label}>
              {idx === menuItems?.length - 1 && (
                <div className="border-t border-border my-1" />
              )}
              <button
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left font-caption text-sm transition-all duration-200 hover:bg-muted press-effect focus:outline-none focus-visible:bg-muted ${
                  item?.danger ? 'text-destructive hover:text-destructive' : 'text-card-foreground'
                }`}
                onClick={() => { item?.action(); setOpen(false); }}
                role="menuitem"
              >
                <Icon name={item?.icon} size={16} color="currentColor" strokeWidth={2} />
                <span>{item?.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}